import { Info } from 'lucide-react';
import { Switch } from '@/components/atoms/switch';
import { HintTooltip } from '@/components/molecules';

interface AiTogglesPanelProps {
  values: {
    aiPersonalization: boolean;
    aiSendTimeOptimization: boolean;
    aiChannelSelection: boolean;
    aiVoiceFallback: boolean;
  };
  onChange: (key: keyof AiTogglesPanelProps['values'], next: boolean) => void;
  readOnly?: boolean;
}

const TOGGLES: Array<{
  key: keyof AiTogglesPanelProps['values'];
  label: string;
  description: string;
}> = [
  {
    key: 'aiPersonalization',
    label: 'AI personalization',
    description: 'Generate per-customer copy at send time. Falls back to template on error.',
  },
  {
    key: 'aiSendTimeOptimization',
    label: 'Send-time optimization',
    description: 'Shift scheduledAt by ±6h based on each customer’s engagement history.',
  },
  {
    key: 'aiChannelSelection',
    label: 'Adaptive channel selection',
    description: 'Skip channels with 0% open/reply over the last 3 attempts.',
  },
  {
    key: 'aiVoiceFallback',
    label: 'AI voice fallback',
    description:
      'Include the cadence’s VOICE escalation step. When off, voice steps are skipped so the sequence stays SMS/email only.',
  },
];

export function AiTogglesPanel({ values, onChange, readOnly }: AiTogglesPanelProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <h3 className="text-sm font-medium">AI features</h3>
        <HintTooltip>Toggle which AI capabilities apply to this reminder type.</HintTooltip>
      </div>

      <div className="space-y-2 rounded-md border border-border bg-card p-3">
        {TOGGLES.map((toggle) => (
          <div
            key={toggle.key}
            className="flex items-center justify-between gap-3 border-b border-border/40 py-2 last:border-b-0 last:pb-0 first:pt-0"
          >
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium">{toggle.label}</p>
              <HintTooltip>{toggle.description}</HintTooltip>
            </div>
            <Switch
              checked={values[toggle.key]}
              onCheckedChange={(next) => onChange(toggle.key, next)}
              disabled={readOnly}
              aria-label={toggle.label}
            />
          </div>
        ))}
      </div>

      <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Voice fallback takes effect immediately. Personalization, send-time optimization and
          adaptive channel selection activate once ai-operations is connected; until then they stay
          configured and messages fall back to templates on their default schedule.
        </span>
      </div>
    </div>
  );
}
